/*
 * store-status.js — <store-status> custom element.
 * Renders a live "Open now / Closed" chip computed in the store's timezone
 * (America/Toronto) from a weekly schedule passed via data-hours. Fails safe on
 * Ontario statutory holidays (auto-detected, incl. Good Friday via Easter math) —
 * shows "Closed — holiday" instead of ever wrongly saying "Open". A manual override
 * (data-override = auto | open | closed) covers the exceptions. Re-checks every minute
 * so it flips at open/close time without a page reload. Logic unit-tested in-browser.
 */
if ( typeof customElements.get('store-status') === 'undefined' ) {

  class StoreStatus extends HTMLElement {

    connectedCallback(){
      this.hours = this._parseHours(this.dataset.hours);
      this.tz = this.dataset.tz || 'America/Toronto';
      this.override = ( this.dataset.override || 'auto' ).toLowerCase();
      this.wdN = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      this._render();
      this._timer = setInterval(() => this._render(), 60000);
    }

    disconnectedCallback(){ if ( this._timer ) clearInterval(this._timer); }

    // "0,720,1080;1,630,1140;..." → hours[day] = [openMin, closeMin]; day 0 = Sun.
    _parseHours(s){
      const h = [];
      ( s || '' ).split(';').forEach(seg => {
        const [d, o, c] = seg.split(',').map(Number);
        if ( !isNaN(d) ) h[d] = [o, c];
      });
      return h;
    }

    // Current wall-clock date/time in the store's timezone (works for any viewer TZ).
    _tParts(d){
      const p = new Intl.DateTimeFormat('en-CA', { timeZone: this.tz, year:'numeric', month:'2-digit', day:'2-digit', weekday:'short', hour:'2-digit', minute:'2-digit', hour12:false }).formatToParts(d);
      const g = t => p.find(x => x.type === t).value;
      const wd = { Sun:0, Mon:1, Tue:2, Wed:3, Thu:4, Fri:5, Sat:6 };
      return { y:+g('year'), m:+g('month'), day:+g('day'), wd: wd[g('weekday')], mins: (+g('hour'))*60 + (+g('minute')) };
    }

    _nthMon(y, m, n){ const f = new Date(Date.UTC(y, m-1, 1)).getUTCDay(); return 1 + ((1 - f + 7) % 7) + (n-1)*7; }

    _easter(y){
      const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,mm=Math.floor((a+11*h+22*l)/451);
      return [ Math.floor((h+l-7*mm+114)/31), ((h+l-7*mm+114)%31)+1 ];
    }

    // Ontario statutory holidays + the August Civic Holiday (store usually closed).
    _isHoliday(y, m, day){
      if ( (m===1&&day===1) || (m===7&&day===1) || (m===12&&day===25) || (m===12&&day===26) ) return true; // New Year / Canada Day / Christmas / Boxing
      if ( m===2  && day===this._nthMon(y,2,3)  ) return true; // Family Day (3rd Mon Feb)
      if ( m===8  && day===this._nthMon(y,8,1)  ) return true; // Civic (1st Mon Aug)
      if ( m===9  && day===this._nthMon(y,9,1)  ) return true; // Labour Day (1st Mon Sep)
      if ( m===10 && day===this._nthMon(y,10,2) ) return true; // Thanksgiving (2nd Mon Oct)
      if ( m===5 ) { let v=24; while ( new Date(Date.UTC(y,4,v)).getUTCDay() !== 1 ) v--; if ( day===v ) return true; } // Victoria Day (Mon on/before May 24)
      const [em, ed] = this._easter(y); const gf = new Date(Date.UTC(y, em-1, ed-2)); // Good Friday = Easter - 2
      if ( m === gf.getUTCMonth()+1 && day === gf.getUTCDate() ) return true;
      return false;
    }

    _fmt(mins){ const h=Math.floor(mins/60), m=mins%60, ap=h>=12?'pm':'am', hr=h%12||12; return m===0 ? `${hr}${ap}` : `${hr}:${String(m).padStart(2,'0')}${ap}`; }

    // Next day the store is actually open (skips holidays), up to a week out.
    _nextOpen(p){
      for ( let i=1; i<=8; i++ ) {
        const fd = new Date(Date.UTC(p.y, p.m-1, p.day+i));
        const fy=fd.getUTCFullYear(), fm=fd.getUTCMonth()+1, fday=fd.getUTCDate(), fwd=fd.getUTCDay();
        const nt = this.hours[fwd];
        if ( nt && !this._isHoliday(fy, fm, fday) ) return { dn: i===1 ? 'tomorrow' : this.wdN[fwd], t: nt[0] };
      }
      return null;
    }

    _compute(){
      const p = this._tParts(new Date());
      const ov = this.override;
      const t = this.hours[p.wd];
      if ( ov === 'open' ) return { open:true, text: t ? `Open now · till ${this._fmt(t[1])}` : 'Open now' };
      const holiday = ov !== 'closed' && this._isHoliday(p.y, p.m, p.day);
      if ( ov === 'closed' || holiday ) {
        const nx = this._nextOpen(p);
        return { open:false, text: (holiday ? 'Closed — holiday' : 'Closed today') + (nx ? ` · opens ${nx.dn} ${this._fmt(nx.t)}` : '') };
      }
      if ( t && p.mins >= t[0] && p.mins < t[1] ) return { open:true, text: `Open now · till ${this._fmt(t[1])}` };
      if ( t && p.mins < t[0] ) return { open:false, text: `Closed · opens ${this._fmt(t[0])}` };
      const nx = this._nextOpen(p);
      return { open:false, text: 'Closed' + (nx ? ` · opens ${nx.dn} ${this._fmt(nx.t)}` : '') };
    }

    _render(){
      const s = this._compute();
      const dot = this.querySelector('[data-store-status-dot]');
      const line1 = this.querySelector('[data-store-status-line1]');
      if ( dot ) dot.className = 'store-status__dot ' + ( s.open ? 'is-open' : 'is-closed' );
      if ( line1 ) line1.textContent = s.text;
      this.setAttribute('data-open', s.open ? '1' : '0');
    }

  }

  customElements.define('store-status', StoreStatus);
}
